import Vue from "vue";
import axios from "axios";
import Nprogress from "nprogress";

import ErrorHandler from "@/helpers/handlers/error";
import {
  displayTwoFactorError,
  getTwoFactorErrorCode
} from "@/helpers/handlers/two-factor-error";
import usersService from "@/services/users";
import router from "@/router";
import { i18n } from "@/main";
import { gtmEvent__removeUserData } from "@/helpers/gtm-events";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";
import * as Sentry from "@sentry/vue";
import mixpanel from "mixpanel-browser";

import posthog from "posthog-js";
import {
  ensureCurrentSession,
  getSessionId,
  invalidateSession
} from "@/helpers/auth-session";

let logoutRequest = Promise.resolve();

const clearSession = commit => {
  invalidateSession();
  localStorage.removeItem(LOCAL_STORAGE_KEYS.COMPANY_ID);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.LOCATIONS_IMPORT_STATUS);
  commit("CLEAR_STORE", null, { root: true });
};

const runAnalytics = callbacks => {
  callbacks.forEach(callback => {
    try {
      callback();
    } catch (_error) {
      // An unavailable analytics SDK must not interrupt a session change.
    }
  });
};

export default {
  _handleAuthSuccess({ commit }, { token, email, method = "email" }) {
    if (!token) throw new Error("Missing authentication token.");
    clearSession(commit);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.USER,
      JSON.stringify({ token, email })
    );
    commit("LOGIN_USER", { token, email });
    Vue.notify({
      group: "default",
      type: "success",
      text: i18n.t("message.notification.userLoggedInSuccess")
    });

    runAnalytics([
      () => gtmEvent__removeUserData(),
      () => Sentry.configureScope(scope => scope.setUser(null)),
      () => Vue.gtm.trackCustomEvent({ event: GTM_EVENT_NAME.LOGIN, method })
    ]);
  },

  _handleAuthError({ commit }) {
    clearSession(commit);
  },

  async authRequest({ commit, dispatch }, { data, serviceMethod }) {
    const sessionId = getSessionId();
    Nprogress.start();
    try {
      // Serializing login with server logout also protects re-login to the same
      // account, whose token the backend may otherwise invalidate too late.
      await logoutRequest;
      ensureCurrentSession(sessionId);
      const user = await serviceMethod(data);
      ensureCurrentSession(sessionId);
      const email = data.email;
      if (user.data.mfa_required) {
        return { ...user.data, email };
      }

      const token = user.data.key;
      await dispatch("_handleAuthSuccess", { token, email, method: "email" });
      return null;
    } catch (error) {
      if (axios.isCancel(error)) {
        ErrorHandler.displayError(error);
        throw error;
      }
      dispatch("_handleAuthError");
      ErrorHandler.displayError(error);
    } finally {
      Nprogress.done();
    }
  },

  async signUpWithLoginAndPassword({ dispatch }, data) {
    Vue.gtm.trackCustomEvent({
      event: GTM_EVENT_NAME.SIGN_UP,
      method: "email",
      marketing_consent: data.is_marketing_consent
    });

    return dispatch("authRequest", {
      data,
      serviceMethod: usersService.registerWithEmailAndPassword
    });
  },

  async acceptInvitation({ dispatch }, data) {
    return dispatch("authRequest", {
      data,
      serviceMethod: usersService.acceptInvitation
    });
  },

  async signInWithEmailAndPassword({ dispatch }, data) {
    return dispatch("authRequest", {
      data,
      serviceMethod: usersService.loginWithEmailAndPassword
    });
  },

  async completeTwoFactorLogin({ dispatch }, { email, ...data }) {
    const sessionId = getSessionId();
    Nprogress.start();
    try {
      await logoutRequest;
      ensureCurrentSession(sessionId);
      const response = await usersService.completeTwoFactorLogin(data);
      ensureCurrentSession(sessionId);
      await dispatch("_handleAuthSuccess", {
        token: response.data.key,
        email,
        method: "email"
      });
      return { success: true };
    } catch (error) {
      displayTwoFactorError(error);
      return {
        success: false,
        errorCode: getTwoFactorErrorCode(error)
      };
    } finally {
      Nprogress.done();
    }
  },

  updateAuthToken({ commit, state }, token) {
    invalidateSession();
    const email = state.user.email;
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.USER,
      JSON.stringify({ token, email })
    );
    commit("LOGIN_USER", { token, email });
  },

  // TODO: Sign with FB
  signInUserWithFacebook() {},

  // TODO: Sign with Google
  signInUserWithGoogle() {},

  logout({ commit, state }, redirect = true) {
    const token = state.user.token;
    localStorage.removeItem(LOCAL_STORAGE_KEYS.DEMO_USER);
    clearSession(commit);

    if (token) {
      logoutRequest = Promise.resolve()
        .then(() => usersService.logout(token))
        .catch(() => {
          // Local logout is complete even when the server is unavailable.
          // The request has a timeout so it cannot indefinitely block login.
        });
    }

    runAnalytics([
      () => Vue.gtm.trackCustomEvent({ event: GTM_EVENT_NAME.LOGOUT }),
      () => this._vm.$setUserProperties({ login_status: "logged_out" }),
      () => gtmEvent__removeUserData(),
      () => Sentry.configureScope(scope => scope.setUser(null)),
      () => posthog.reset(true),
      () => mixpanel.core.reset()
    ]);

    if (redirect) {
      router.push({ name: "session-login" }).catch(() => {});
    }
    return logoutRequest;
  },

  async authWithoutRequest(
    { commit, dispatch },
    { token, email, method = "sso" }
  ) {
    const sessionId = getSessionId();
    Nprogress.start();
    try {
      await logoutRequest;
      ensureCurrentSession(sessionId);
      await dispatch("_handleAuthSuccess", { token, email, method });
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      dispatch("_handleAuthError");
      ErrorHandler.displayError(error);
    } finally {
      Nprogress.done();
    }
  },

  async _retrieveSamlToken() {
    const { data } = await usersService.retrieveSamlToken();
    return data;
  },

  async signInUserWithSso({ dispatch }) {
    const sessionId = getSessionId();
    await logoutRequest;
    ensureCurrentSession(sessionId);
    try {
      const loginData = await dispatch("_retrieveSamlToken");
      ensureCurrentSession(sessionId);
      const { token, user } = loginData;

      return dispatch("authWithoutRequest", {
        token,
        email: user.email,
        method: "sso"
      });
    } catch (error) {
      ensureCurrentSession(sessionId);
      throw error;
    }
  },

  setPlanRestrictions({ commit }, planRestrictions) {
    commit("SET_PLAN_RESTRICTIONS", planRestrictions);
  }
};
